import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { map } from 'rxjs';
import { environment } from '../../environments/environment';

export interface SeriesMasterDto {
  id: number;
  seriesTypeID: number;
  seriesName?: string | null;
  separator?: string | null;
  resetType?: string | null;
  typeName?: string | null;
}

export interface SeriesFormatDto {
  id: number;
  seriesId: number;
  tokenType?: string | null;
  tokenValue?: string | null;
  tokenOrder: number;
  padding?: number | null;
  seriesName?: string | null;
}

/** The get-all-master/get-all-format endpoints return PascalCase keys; map them to the camelCase DTOs. */
function mapMaster(x: any): SeriesMasterDto {
  return {
    id: x.Id ?? x.id,
    seriesTypeID: x.SeriesTypeID ?? x.seriesTypeID,
    seriesName: x.SeriesName ?? x.seriesName ?? null,
    separator: x.Separator ?? x.separator ?? null,
    resetType: x.ResetType ?? x.resetType ?? null,
    typeName: x.TypeName ?? x.typeName ?? null,
  };
}

function mapFormat(x: any): SeriesFormatDto {
  return {
    id: x.Id ?? x.id,
    seriesId: x.SeriesId ?? x.seriesId,
    tokenType: x.TokenType ?? x.tokenType ?? null,
    tokenValue: x.TokenValue ?? x.tokenValue ?? null,
    tokenOrder: x.TokenOrder ?? x.tokenOrder,
    padding: x.Padding ?? x.padding ?? null,
    seriesName: x.SeriesName ?? x.seriesName ?? null,
  };
}

/** Normalize the response envelope's `data` array with the given mapper. */
function mapEnvelope<T>(fn: (x: any) => T) {
  return (res: any) => {
    const data = res?.data ?? res ?? [];
    const mapped = Array.isArray(data) ? data.map(fn) : [];
    return res && typeof res === 'object' && 'data' in res ? { ...res, data: mapped } : mapped;
  };
}

@Injectable({ providedIn: 'root' })
export class SeriesService {
  private http = inject(HttpClient);
  private readonly typeUrl = `${environment.apiUrl}/SeriesTypeMaster`;
  private readonly masterUrl = `${environment.apiUrl}/SeriesMaster`;
  // These endpoints take a raw JSON string body ([FromBody] string).
  private readonly jsonHeaders = new HttpHeaders({ 'Content-Type': 'application/json' });

  // ================= SERIES TYPES =================
  getAllTypes() {
    return this.http.get(`${this.typeUrl}/GetAll`);
  }

  getTypeById(id: number) {
    return this.http.get(`${this.typeUrl}/GetById`, { params: { id } });
  }

  insertType(name: string) {
    return this.http.post(`${this.typeUrl}/InsertSeriesType`, JSON.stringify(name), {
      headers: this.jsonHeaders,
    });
  }

  updateType(id: number, name: string) {
    return this.http.post(`${this.typeUrl}/UpdateSeriesType`, JSON.stringify(name), {
      headers: this.jsonHeaders,
      params: { id },
    });
  }

  deleteType(id: number) {
    return this.http.get(`${this.typeUrl}/DeleteSeriesType`, { params: { id } });
  }

  // ================= SERIES MASTER =================
  getAllMasters() {
    return this.http.get(`${this.masterUrl}/get-all-master`).pipe(map(mapEnvelope(mapMaster)));
  }

  insertMaster(dto: SeriesMasterDto) {
    return this.http.post(`${this.masterUrl}/insert-master`, dto);
  }

  updateMaster(dto: SeriesMasterDto) {
    return this.http.post(`${this.masterUrl}/update-master`, dto);
  }

  deleteMaster(id: number) {
    return this.http.get(`${this.masterUrl}/delete-master`, { params: { id } });
  }

  // ================= SERIES FORMAT =================
  getAllFormats() {
    return this.http.get(`${this.masterUrl}/get-all-format`).pipe(map(mapEnvelope(mapFormat)));
  }

  insertFormat(dto: SeriesFormatDto) {
    return this.http.post(`${this.masterUrl}/insert-format`, dto);
  }

  updateFormat(dto: SeriesFormatDto) {
    return this.http.post(`${this.masterUrl}/update-format`, dto);
  }

  deleteFormat(id: number) {
    return this.http.get(`${this.masterUrl}/delete-format`, { params: { id } });
  }
}
